'use client';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';

// BACKEND INTEGRATION: GET /api/admin/apartments/availability
const apartments = [
  {
    id: 'apt-001',
    name: 'Sunset Terrace Suite',
    shortName: 'Suite',
    color: 'bg-primary',
    textColor: 'text-primary',
    bgLight: 'bg-primary/10',
    bookedDays: [3, 4, 5, 6, 7, 18, 19, 20, 21, 22, 28, 29, 30],
    blockedDays: [10, 11],
  },
  {
    id: 'apt-002',
    name: 'Ocean Breeze Loft',
    shortName: 'Loft',
    color: 'bg-secondary',
    textColor: 'text-secondary',
    bgLight: 'bg-secondary/10',
    bookedDays: [1, 2, 8, 9, 14, 15, 16, 25, 26, 27],
    blockedDays: [20],
  },
  {
    id: 'apt-003',
    name: 'Garden Villa Escape',
    shortName: 'Villa',
    color: 'bg-emerald-500',
    textColor: 'text-emerald-600',
    bgLight: 'bg-emerald-50',
    bookedDays: [2, 3, 4, 5, 6, 7, 12, 13, 14, 15, 16, 17, 22, 23, 24, 25, 26, 27, 28],
    blockedDays: [],
  },
  {
    id: 'apt-004',
    name: 'Rooftop Haven',
    shortName: 'Roof',
    color: 'bg-foreground',
    textColor: 'text-foreground',
    bgLight: 'bg-muted',
    bookedDays: [5, 6, 7, 8, 9, 10, 18, 19, 20, 21],
    blockedDays: [25, 26, 27, 28, 29, 30, 31],
  },
];

const DAY_NAMES_SHORT = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function MiniCalendar({
  apt,
  year,
  month,
}: {
  apt: typeof apartments[0];
  year: number;
  month: number;
}) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [localBlocked, setLocalBlocked] = useState<number[]>(apt.blockedDays);

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = (firstDay.getDay() + 6) % 7;
  const today = 14; // May 14 2026

  const days: (number | null)[] = [];
  for (let i = 0; i < startPad; i++) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);

  // BACKEND INTEGRATION: PATCH /api/admin/apartments/:id/block-date
  const toggleBlock = (day: number) => {
    if (apt.bookedDays.includes(day)) {
      toast.error('Cannot block a date that already has a booking.');
      return;
    }
    const newBlocked = localBlocked.includes(day)
      ? localBlocked.filter((d) => d !== day)
      : [...localBlocked, day];
    setLocalBlocked(newBlocked);
    toast.success(
      localBlocked.includes(day)
        ? `${MONTH_NAMES[month]} ${day} unblocked for ${apt.name}`
        : `${MONTH_NAMES[month]} ${day} blocked for ${apt.name}`
    );
    setSelectedDay(null);
  };

  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {DAY_NAMES_SHORT.map((d, i) => (
          <div key={`dname-${apt.id}-${i}`} className="text-center text-[9px] font-700 uppercase text-muted-foreground py-0.5">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((day, idx) => {
          if (!day) return <div key={`pad-${apt.id}-${idx}`} />;
          const isBooked = apt.bookedDays.includes(day);
          const isBlocked = localBlocked.includes(day);
          const isToday = day === today && month === 4;
          const isPast = day < today && month <= 4;
          const isSelected = selectedDay === day;

          return (
            <button
              key={`cal-${apt.id}-${day}`}
              onClick={() => !isPast && !isBooked && setSelectedDay(isSelected ? null : day)}
              disabled={isPast}
              className={`
                aspect-square flex items-center justify-center text-[10px] font-500 rounded transition-all duration-150
                ${isPast ? 'text-muted-foreground/30 cursor-not-allowed' :
                  isBooked ? 'bg-primary/20 text-primary cursor-default': isBlocked ?'bg-red-100 text-red-500': isToday ?'ring-2 ring-primary text-primary font-700': isSelected ?'bg-foreground text-white': 'hover:bg-muted text-foreground cursor-pointer'}
              `}
              title={isBooked ? 'Booked' : isBlocked ? 'Blocked by owner' : isPast ? '' : 'Click to block/unblock'}
            >
              {day}
            </button>
          );
        })}
      </div>
      {selectedDay && !apt.bookedDays.includes(selectedDay) && (
        <div className="mt-3 p-2.5 bg-muted rounded-xl flex items-center justify-between gap-2 animate-slide-up">
          <span className="text-xs text-foreground font-600">{MONTH_NAMES[month]} {selectedDay}</span>
          <div className="flex gap-2">
            <button
              onClick={() => toggleBlock(selectedDay)}
              className={`flex items-center gap-1 text-xs font-600 px-2.5 py-1.5 rounded-lg transition-colors ${
                localBlocked.includes(selectedDay)
                  ? 'bg-green-100 text-green-700 hover:bg-green-200' :'bg-red-100 text-red-600 hover:bg-red-200'
              }`}
            >
              {localBlocked.includes(selectedDay) ? <><Unlock size={11} /> Unblock</> : <><Lock size={11} /> Block</>}
            </button>
            <button
              onClick={() => setSelectedDay(null)}
              className="text-xs text-muted-foreground px-2 py-1.5 rounded-lg hover:bg-border transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApartmentAvailabilityCards() {
  const [monthOffset, setMonthOffset] = useState(0);
  const baseYear = 2026;
  const baseMonth = 4;
  const month = (baseMonth + monthOffset) % 12;
  const year = baseYear + Math.floor((baseMonth + monthOffset) / 12);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-700 text-foreground">Availability Calendars</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Click any available date to block or unblock it</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonthOffset((o) => Math.max(0, o - 1))}
            disabled={monthOffset === 0}
            className="p-1.5 rounded-lg hover:bg-muted disabled:opacity-30 transition-colors border border-border"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-600 text-foreground min-w-[100px] text-center">
            {MONTH_NAMES[month]} {year}
          </span>
          <button
            onClick={() => setMonthOffset((o) => o + 1)}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors border border-border"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4">
        {[
          { color: 'bg-primary/20', label: 'Booked' },
          { color: 'bg-red-100', label: 'Blocked by owner' },
          { color: 'bg-white border border-border', label: 'Available' },
        ].map((item) => (
          <div key={`legend-${item.label}`} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded ${item.color}`} />
            <span className="text-xs text-muted-foreground">{item.label}</span>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {apartments.map((apt) => {
          const bookedCount = apt.bookedDays.filter((d) => {
            const lastDay = new Date(year, month + 1, 0).getDate();
            return d <= lastDay;
          }).length;
          const totalDays = new Date(year, month + 1, 0).getDate();
          const occPct = Math.round((bookedCount / totalDays) * 100);

          return (
            <div key={apt.id} className="card-base p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${apt.color}`} />
                <h3 className="font-700 text-sm text-foreground truncate flex-1">{apt.name}</h3>
                <span className={`text-xs font-700 tabular-nums ${apt.textColor}`}>{occPct}%</span>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${apt.color}`} style={{ width: `${occPct}%` }} />
                </div>
                <span className="text-[10px] text-muted-foreground tabular-nums">{bookedCount}/{totalDays}d</span>
              </div>
              <MiniCalendar apt={apt} year={year} month={month} />
            </div>
          );
        })}
      </div>
    </div>
  );
}